import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useEffect, useMemo, useState, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPostFunc, postDisLikeFunc, postLikeFunc } from 'v4/lib/api/post';
import { useDisclosure } from '@mybridge/ui/hooks';
import {
  deleteComment,
  disLikeComment,
  fetchComments,
  likeComment,
} from 'packages/mybridgev4/store/actions/comment.actions';
import { PostBoxContext } from 'v4/components/post/context';

export const PostCommentRowContext = createContext({});

// Helper function to find comment in nested structure (including replies)
const findCommentInList = (commentList, targetId) => {
  if (!commentList || !Array.isArray(commentList)) return null;
  
  for (const c of commentList) {
    if (c?.id === targetId) return c;
    if (c?.comment_replies?.length > 0) {
      const found = findCommentInList(c.comment_replies, targetId);
      if (found) return found;
    }
  }
  return null;
};

export const usePostCommentRowContext = (props) => {
  const {
    post,
    comment,
    initialFetchEnabled = false,
    refetch,
    level,
    commentList
  } = props ?? {};
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const [commentState, setCommentState] = useState(comment);
  const [mode, setMode] = useState('view'); // view | edit
  const [commentBoxExpanded, setCommentBoxExapnded] = useState(false);
  const {
    comments
  } = useContext(PostBoxContext);
  
  const deleteCommentModal = useDisclosure();
  const dispatch = useDispatch();
  const { reply_count, like_count, dislike_count, id } = commentState ?? {};
  const prevCommentRef = useRef(comment);

  const { id: uid } = userProfileInfo ?? {};
  const isMyComment = commentState?.owner?.id === userProfileInfo?.id;
  const commentOwner = comment?.comment_replies?.length > 0 ? comment?.owner : null
  
  useEffect(() => {
    if (comment?.id) {
      // First, try to find updated comment from PostBoxContext comments array
      // Also check commentList prop for nested replies
      const updatedComment = 
        (comments?.length > 0 ? findCommentInList(comments, comment.id) : null) ||
        (commentList?.length > 0 ? findCommentInList(commentList, comment.id) : null);
      
      // Use updated comment from context if available, otherwise use the prop
      const commentToUse = updatedComment || comment;
      
      const prevComment = prevCommentRef.current;
      // Check if comment has actually changed by comparing key fields that would update on edit
      // This is more reliable than JSON.stringify which can fail with property order or nested objects
      const hasChanged = 
        !prevComment ||
        prevComment?.id !== commentToUse?.id ||
        prevComment?.body !== commentToUse?.body ||
        prevComment?.gif !== commentToUse?.gif ||
        prevComment?.updated_at !== commentToUse?.updated_at ||
        prevComment?.like_count !== commentToUse?.like_count ||
        prevComment?.dislike_count !== commentToUse?.dislike_count ||
        prevComment?.reply_count !== commentToUse?.reply_count ||
        prevComment?.comment_replies?.length !== commentToUse?.comment_replies?.length ||
        JSON.stringify(prevComment?.media || []) !== JSON.stringify(commentToUse?.media || []);
      
      if (hasChanged) {
        setCommentState(commentToUse);
        prevCommentRef.current = commentToUse;
      }
    }
  }, [comment, comments, commentList]);

  const hasInsights = useMemo(() => {
    return reply_count > 0 || like_count > 0 || dislike_count > 0;
  }, [commentState]);

  const handleDeleteComment = () => {
    const payload = {
      id: commentState?.id,
    };
    mutateDeleteComment(payload);
  };

  // query for deleting comment
  const { mutate: mutateDeleteComment, isLoading: deleteCommentLoading } =
    useMutation((body) => dispatch(deleteComment(body)), {
      onSuccess: (data) => {
        deleteCommentModal.onClose();
        refetch?.();
        setCommentState({ ...commentState, removed: true });
      },
      onError: (error) => {
        console.error(error);
        deleteCommentModal.onClose();
      },
    });

  // query for liking comment
  const { mutate: like, isLoading: likeLoading } = useMutation(
    (body) => dispatch(likeComment(body)),
    {
      onSuccess: (data) => {
        // Update local state immediately for real-time UI update
        const isUserLiked = commentState?.liked_by?.find((el) => el.id === uid);
        
        if (isUserLiked) {
          // Remove user from liked_by array and decrease like_count
          const updatedLikedBy = commentState.liked_by.filter((el) => el.id !== uid);
          setCommentState(prev => ({
            ...prev,
            liked_by: updatedLikedBy,
            like_count: Math.max(0, (prev.like_count || 0) - 1)
          }));
        } else {
          // Add user to liked_by array and increase like_count
          const updatedLikedBy = [...(commentState.liked_by || []), { id: uid, ...userProfileInfo }];
          // Remove from disliked_by if present
          const updatedDislikedBy = commentState.dislike_by?.filter((el) => el.id !== uid) || [];
          setCommentState(prev => ({
            ...prev,
            liked_by: updatedLikedBy,
            dislike_by: updatedDislikedBy,
            like_count: (prev.like_count || 0) + 1,
            dislike_count: updatedDislikedBy.length < (prev.dislike_by?.length || 0) 
              ? Math.max(0, (prev.dislike_count || 0) - 1) 
              : (prev.dislike_count || 0)
          }));
        }
        
        // Still call refetch for server sync, but UI is already updated
        refetch?.();
      },
      onError: (error) => {
        console.error(error);
        // Revert optimistic update on error
        setCommentState(comment);
      },
    }
  );

  // query for disliking comment
  const { mutate: dislike, isLoading: dislikeLoading } = useMutation(
    (body) => dispatch(disLikeComment(body)),
    {
      onSuccess: (data) => {
        // Update local state immediately for real-time UI update
        const isUserDisliked = commentState?.dislike_by?.find((el) => el.id === uid);
        
        if (isUserDisliked) {
          // Remove user from dislike_by array and decrease dislike_count
          const updatedDislikedBy = commentState.dislike_by.filter((el) => el.id !== uid);
          setCommentState(prev => ({
            ...prev,
            dislike_by: updatedDislikedBy,
            dislike_count: Math.max(0, (prev.dislike_count || 0) - 1)
          }));
        } else {
          // Add user to dislike_by array and increase dislike_count
          const updatedDislikedBy = [...(commentState.dislike_by || []), { id: uid, ...userProfileInfo }];
          // Remove from liked_by if present
          const updatedLikedBy = commentState.liked_by?.filter((el) => el.id !== uid) || [];
          setCommentState(prev => ({
            ...prev,
            dislike_by: updatedDislikedBy,
            liked_by: updatedLikedBy,
            dislike_count: (prev.dislike_count || 0) + 1,
            like_count: updatedLikedBy.length < (prev.liked_by?.length || 0) 
              ? Math.max(0, (prev.like_count || 0) - 1) 
              : (prev.like_count || 0)
          }));
        }
        
        // Still call refetch for server sync, but UI is already updated
        refetch?.();
      },
      onError: (error) => {
        console.error(error);
        // Revert optimistic update on error
        setCommentState(comment);
      },
    }
  );

  const handleLike = () => {
    const isUserLiked = commentState?.liked_by?.find((el) => el.id === uid);
    if (isUserLiked) {
      like({ id, requestBody: { remove: true } });
    } else {
      like({ id, requestBody: { remove: false } });
    }
  };

  const handleDislike = () => {
    const isUserDisliked = commentState?.dislike_by?.find(
      (el) => el.id === uid
    );
    if (isUserDisliked) {
      dislike({ id, requestBody: { remove: true } });
    } else {
      dislike({ id, requestBody: { remove: false } });
    }
  };
  const { gif, media } = commentState ?? {};
  const hasMedia = gif?.length > 0 || media?.length > 0;

  // console.log('commentList', comments);

  return {
    hasInsights,
    handleDislike,
    handleLike,
    refetch,
    likeLoading,
    dislikeLoading,
    // isLoading: isLoading || isFetching,
    hasMedia,
    comment: commentState,
    level,
    mode,
    setMode,
    setCommentBoxExapnded,
    commentBoxExpanded,
    post,
    isMyComment,
    deleteComment: handleDeleteComment,
    deleteCommentLoading,
    deleteCommentModal,
    commentOwner,
  };
};
